import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../services/firebase";
import { Globals } from "../globals";
import Gemeinde, { gemeindeConverter, ChannelRelease } from "../DTOs/Gemeinde";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import I_IMC_Props from "../infos/I_IMC_Props";
import NotificationCredentialsForm from "./NotificationCredentialsForm";
import ChoirBackfill from "./ChoirBackfill";
import SuperadminClaim from "./SuperadminClaim";
import DeletionRequests from "./DeletionRequests";
import DeleteGemeindeDialog from "./DeleteGemeindeDialog";
import Table from "react-bootstrap/esm/Table";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Card from "react-bootstrap/esm/Card";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";

interface ManagedUser {
    uid: string;
    email: string;
    displayName: string;
    role: string | null;
    gemeindeId: string | null;
    superadmin?: boolean;
    disabled: boolean;
    createdAt?: string;
}

const EMPTY_GEMEINDE_FORM = {
    name: "",
    slug: "",
    holidayRegion: "",
    defaultLanguage: "de",
    choirSheetUrl: "",
};

export default function SuperadminPanel({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isSuperAdmin, loading: authLoading } = useAuth();
    const { setActiveGemeindeId } = useGemeinde();

    // ?tab=deletion opens the deletion requests directly (link in the request mail).
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState<"gemeinden" | "users" | "credentials" | "maintenance" | "deletion">(
        searchParams.get("tab") === "deletion" ? "deletion" : "gemeinden"
    );
    const [gemeinden, setGemeinden] = useState<Gemeinde[]>([]);
    const [users, setUsers] = useState<ManagedUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [gemeindeForm, setGemeindeForm] = useState({ ...EMPTY_GEMEINDE_FORM });
    const [userForm, setUserForm] = useState({ email: "", role: Globals.ROLE_MEMBER, gemeindeId: "" });
    const [gemeindeToDelete, setGemeindeToDelete] = useState<Gemeinde | null>(null);
    const [drafts, setDrafts] = useState<Record<string, { role: string; gemeindeId: string }>>({});

    const notify = useCallback((variant: string, msg: string) => {
        setVariant(variant);
        setMessage(msg);
        setShowMessage(true);
    }, [setVariant, setMessage, setShowMessage]);

    const loadGemeinden = useCallback(async () => {
        const snap = await getDocs(collection(db, Globals.GEMEINDEN_COLLECTION).withConverter(gemeindeConverter));
        setGemeinden(snap.docs.map((d) => d.data()).sort((a, b) => a.name.localeCompare(b.name)));
    }, []);

    const loadUsers = useCallback(async () => {
        const listUsers = httpsCallable<unknown, { users: ManagedUser[] }>(functions, "listUsersWithRoles");
        const res = await listUsers({});
        const sorted = res.data.users.sort((a, b) => a.email.localeCompare(b.email));
        setUsers(sorted);
        setDrafts(Object.fromEntries(sorted.map((u) =>
            [u.uid, { role: u.role ?? Globals.ROLE_MEMBER, gemeindeId: u.gemeindeId ?? "" }])));
    }, []);

    useEffect(() => {
        if (authLoading || !isSuperAdmin) return;
        (async () => {
            setLoading(true);
            try {
                await Promise.all([loadGemeinden(), loadUsers()]);
            } catch (err: any) {
                notify("danger", err?.message ?? t("errors.anErrorOccurred"));
            } finally {
                setLoading(false);
            }
        })();
    }, [authLoading, isSuperAdmin, loadGemeinden, loadUsers, notify, t]);

    async function handleCreateGemeinde(e: React.FormEvent) {
        e.preventDefault();
        setBusy(true);
        try {
            const createGemeinde = httpsCallable(functions, "createGemeinde");
            await createGemeinde(gemeindeForm);
            setGemeindeForm({ ...EMPTY_GEMEINDE_FORM });
            await loadGemeinden();
            notify("success", t("superadmin.gemeindeCreated"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    async function handleToggleChannel(g: Gemeinde, key: keyof ChannelRelease) {
        setBusy(true);
        try {
            const channels = { ...g.channels, [key]: !g.channels[key] };
            const setChannels = httpsCallable(functions, "setGemeindeChannels");
            await setChannels({ gemeindeId: g.id, channels });
            await loadGemeinden();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    async function handleGemeindeDeleted() {
        setGemeindeToDelete(null);
        await loadGemeinden();
        await loadUsers();
        notify("success", t("superadmin.gemeindeDeleted"));
    }

    function handleOpenGemeinde(g: Gemeinde) {
        setActiveGemeindeId(g.id);
        navigate(`${Globals.PATH_ADMIN}?tab=preachers`);
    }

    async function handleAssignUser(e: React.FormEvent) {
        e.preventDefault();
        setBusy(true);
        try {
            const setUserAssignment = httpsCallable<object, { created: boolean; invited: boolean }>(functions, "setUserAssignment");
            const { data } = await setUserAssignment(userForm);
            setUserForm({ email: "", role: Globals.ROLE_MEMBER, gemeindeId: "" });
            await loadUsers();
            // A new account gets an invitation mail (GOD-39).
            if (data.created && !data.invited) notify("warning", t("superadmin.invitationFailed"));
            else notify("success", t(data.created ? "superadmin.userInvited" : "superadmin.userAssigned"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    async function handleRemoveUser(u: ManagedUser) {
        if (!window.confirm(t("superadmin.confirmRemoveUser", { email: u.email }) as string)) return;
        setBusy(true);
        try {
            const removeUserAssignment = httpsCallable(functions, "removeUserAssignment");
            await removeUserAssignment({ uid: u.uid });
            await loadUsers();
            notify("success", t("superadmin.userRemoved"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    async function handleResendInvitation(u: ManagedUser) {
        if (!u.gemeindeId || !u.role) return;
        setBusy(true);
        try {
            const setUserAssignment = httpsCallable<object, { invited: boolean }>(functions, "setUserAssignment");
            const { data } = await setUserAssignment({ email: u.email, role: u.role, gemeindeId: u.gemeindeId, resendInvitation: true });
            notify(data.invited ? "success" : "warning", t(data.invited ? "superadmin.invitationSent" : "superadmin.invitationFailed"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    const updateDraft = (uid: string, patch: Partial<{ role: string; gemeindeId: string }>) =>
        setDrafts((d) => ({ ...d, [uid]: { ...d[uid], ...patch } }));

    async function handleSaveRow(u: ManagedUser) {
        const draft = drafts[u.uid];
        if (!draft?.gemeindeId) return;
        setBusy(true);
        try {
            const setUserAssignment = httpsCallable(functions, "setUserAssignment");
            await setUserAssignment({ email: u.email, role: draft.role, gemeindeId: draft.gemeindeId });
            await loadUsers();
            notify("success", t("superadmin.userAssigned"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        } finally {
            setBusy(false);
        }
    }

    const gemeindeName = (id: string | null) => (id ? gemeinden.find((g) => g.id === id)?.name ?? id : "—");

    if (authLoading) {
        return <div className="text-center" style={{ padding: "3rem" }}><Spinner animation="border" /></div>;
    }
    if (!isSuperAdmin) {
        return <div className="gl-content"><Alert variant="danger">{t("admin.notAuthorized")}</Alert></div>;
    }

    return (
        <div className="gl-content">
            <div className="mb-4">
                <div className="gl-meta mb-1">Superadmin</div>
                <h1 className="mb-0">{t("superadmin.title")}</h1>
            </div>

            <div className="mb-3 d-flex flex-wrap gap-2">
                <Button variant={tab === "gemeinden" ? "primary" : "outline-primary"} onClick={() => setTab("gemeinden")}>
                    {t("superadmin.gemeinden")}
                </Button>
                <Button variant={tab === "users" ? "primary" : "outline-primary"} onClick={() => setTab("users")}>
                    {t("superadmin.users")}
                </Button>
                <Button variant={tab === "credentials" ? "primary" : "outline-primary"} onClick={() => setTab("credentials")}>
                    {t("superadmin.credentials")}
                </Button>
                <Button variant={tab === "maintenance" ? "primary" : "outline-primary"} onClick={() => setTab("maintenance")}>
                    {t("superadmin.maintenance")}
                </Button>
                <Button variant={tab === "deletion" ? "primary" : "outline-primary"} onClick={() => setTab("deletion")}>
                    {t("deletionRequests.tab")}
                </Button>
            </div>

            {tab === "maintenance" && (
                <>
                    <SuperadminClaim />
                    <ChoirBackfill />
                </>
            )}

            {tab === "deletion" && !authLoading && isSuperAdmin && <DeletionRequests notify={notify} />}
            <DeleteGemeindeDialog
                gemeinde={gemeindeToDelete}
                onClose={() => setGemeindeToDelete(null)}
                onDeleted={handleGemeindeDeleted}
            />

            {tab === "credentials" && (
                <NotificationCredentialsForm setVariant={setVariant} setMessage={setMessage} setShowMessage={setShowMessage} />
            )}

            {loading && (tab === "gemeinden" || tab === "users") && <Spinner animation="border" />}

            {!loading && tab === "gemeinden" && (
                <>
                    <Card className="mb-4">
                        <Card.Body>
                            <h4>{t("superadmin.newGemeinde")}</h4>
                            <Form onSubmit={handleCreateGemeinde}>
                                <div className="row g-2">
                                    <div className="col-md-4">
                                        <Form.Label>{t("superadmin.gemeindeName")}</Form.Label>
                                        <Form.Control required value={gemeindeForm.name}
                                            onChange={(e) => setGemeindeForm({ ...gemeindeForm, name: e.target.value })} />
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Label>{t("superadmin.slug")}</Form.Label>
                                        <Form.Control required value={gemeindeForm.slug} placeholder={t("superadmin.slugPlaceholder") as string}
                                            onChange={(e) => setGemeindeForm({ ...gemeindeForm, slug: e.target.value })} />
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Label>{t("superadmin.region")}</Form.Label>
                                        <Form.Control value={gemeindeForm.holidayRegion} placeholder="NW"
                                            onChange={(e) => setGemeindeForm({ ...gemeindeForm, holidayRegion: e.target.value })} />
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Label>{t("superadmin.language")}</Form.Label>
                                        <Form.Select value={gemeindeForm.defaultLanguage}
                                            onChange={(e) => setGemeindeForm({ ...gemeindeForm, defaultLanguage: e.target.value })}>
                                            <option value="de">Deutsch</option>
                                            <option value="en">English</option>
                                        </Form.Select>
                                    </div>
                                </div>
                                <Button type="submit" className="mt-3" disabled={busy}>
                                    {t("superadmin.create")} {busy && <Spinner size="sm" animation="grow" as="span" />}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>{t("superadmin.gemeindeName")}</th>
                                <th>{t("superadmin.slug")}</th>
                                <th>E-Mail</th>
                                <th>WhatsApp</th>
                                <th>Push</th>
                                <th>{t("admin.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gemeinden.map((g) => (
                                <tr key={g.id}>
                                    <td>{g.name}</td>
                                    <td><code>{g.slug}</code></td>
                                    <td><Form.Check type="switch" checked={g.channels.emailReleased} disabled={busy}
                                        onChange={() => handleToggleChannel(g, "emailReleased")} /></td>
                                    <td><Form.Check type="switch" checked={g.channels.whatsappReleased} disabled={busy}
                                        onChange={() => handleToggleChannel(g, "whatsappReleased")} /></td>
                                    <td><Form.Check type="switch" checked={g.channels.pushReleased} disabled={busy}
                                        onChange={() => handleToggleChannel(g, "pushReleased")} /></td>
                                    <td className="d-flex gap-2">
                                        <Button size="sm" variant="primary" onClick={() => handleOpenGemeinde(g)}>
                                            {t("superadmin.open")}
                                        </Button>
                                        <Button size="sm" variant="danger" onClick={() => setGemeindeToDelete(g)}>
                                            {t("admin.delete")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}

            {!loading && tab === "users" && (
                <>
                    <Card className="mb-4">
                        <Card.Body>
                            <h4>{t("superadmin.assignUser")}</h4>
                            <Form onSubmit={handleAssignUser}>
                                <div className="row g-2">
                                    <div className="col-md-4">
                                        <Form.Label>{t("auth.email")}</Form.Label>
                                        <Form.Control type="email" required value={userForm.email}
                                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Label>{t("superadmin.gemeinde")}</Form.Label>
                                        <Form.Select required value={userForm.gemeindeId}
                                            onChange={(e) => setUserForm({ ...userForm, gemeindeId: e.target.value })}>
                                            <option value="">—</option>
                                            {gemeinden.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                        </Form.Select>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Label>{t("superadmin.role")}</Form.Label>
                                        <Form.Select value={userForm.role}
                                            onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                                            <option value={Globals.ROLE_MEMBER}>{t("superadmin.roleMember")}</option>
                                            <option value={Globals.ROLE_GEMEINDEADMIN}>{t("superadmin.roleGemeindeadmin")}</option>
                                        </Form.Select>
                                    </div>
                                </div>
                                <Button type="submit" className="mt-3" disabled={busy}>
                                    {t("superadmin.assign")} {busy && <Spinner size="sm" animation="grow" as="span" />}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>E-Mail</th>
                                <th>{t("superadmin.role")}</th>
                                <th>{t("superadmin.gemeinde")}</th>
                                <th>{t("admin.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const isSuper = u.superadmin || u.email === Globals.ADMIN_EMAIL;
                                const draft = drafts[u.uid] ?? { role: Globals.ROLE_MEMBER, gemeindeId: "" };
                                const dirty = draft.role !== (u.role ?? Globals.ROLE_MEMBER)
                                    || draft.gemeindeId !== (u.gemeindeId ?? "");
                                return (
                                    <tr key={u.uid}>
                                        <td>{u.email}{isSuper && " (Superadmin)"}</td>
                                        {isSuper ? (
                                            <>
                                                <td>{u.role ?? "—"}</td>
                                                <td>{gemeindeName(u.gemeindeId)}</td>
                                                <td>—</td>
                                            </>
                                        ) : (
                                            <>
                                                <td>
                                                    <Form.Select size="sm" value={draft.role} disabled={busy}
                                                        onChange={(e) => updateDraft(u.uid, { role: e.target.value })}>
                                                        <option value={Globals.ROLE_MEMBER}>{t("superadmin.roleMember")}</option>
                                                        <option value={Globals.ROLE_GEMEINDEADMIN}>{t("superadmin.roleGemeindeadmin")}</option>
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Form.Select size="sm" value={draft.gemeindeId} disabled={busy}
                                                        onChange={(e) => updateDraft(u.uid, { gemeindeId: e.target.value })}>
                                                        <option value="">—</option>
                                                        {gemeinden.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                                    </Form.Select>
                                                </td>
                                                <td className="d-flex gap-2">
                                                    <Button size="sm" variant="primary" disabled={busy || !dirty || !draft.gemeindeId}
                                                        onClick={() => handleSaveRow(u)}>
                                                        {t("admin.save")}
                                                    </Button>
                                                    {(u.role || u.gemeindeId) && (
                                                        <Button size="sm" variant="outline-danger" disabled={busy}
                                                            onClick={() => handleRemoveUser(u)}>
                                                            {t("superadmin.unassign")}
                                                        </Button>
                                                    )}
                                                    {u.role && u.gemeindeId && (
                                                        <Button size="sm" variant="outline-secondary" disabled={busy}
                                                            onClick={() => handleResendInvitation(u)}>
                                                            {t("superadmin.resendInvitation")}
                                                        </Button>
                                                    )}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    );
}
